import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// 'all' applies no status filter; 'active'/'archived' map to ProjectStatus.
export type ArchiveStatusFilter = 'all' | 'active' | 'archived';
// 'all' applies no billable filter; 'true'/'false' map to defaultBillable boolean.
export type BillableFilter = 'all' | 'true' | 'false';
export type SortDirection = 'asc' | 'desc';

export type AdminClientDetailPageState = {
  // UC-42 — Projects sub-section filters / sort.
  projectsFilters: {
    nameSearch: string;
    archiveStatus: ArchiveStatusFilter;
    billableDefault: BillableFilter;
  };
  projectsSort: {
    field: 'name';
    direction: SortDirection;
  };
  // UC-39 — inline rename form state.
  renameMode: boolean;
  // UC-39/40/41 fields — renameDraft seeded on rename entry; archive/delete
  // confirms are stubs for the sibling UCs not yet implemented.
  renameDraft: { name: string } | null;
  pendingArchiveConfirm: boolean;
  pendingDeleteConfirm: boolean;
  // UC-43 — Create Project modal.
  createProjectModalOpen: boolean;
  createProjectDraft: {
    name: string;
    displayColour: string;
    defaultBillable: boolean;
  };
  submitInProgress: boolean;
  lastErrorKey: string | null;
};

const initialState: AdminClientDetailPageState = {
  projectsFilters: {
    nameSearch: '',
    archiveStatus: 'all',
    billableDefault: 'all',
  },
  projectsSort: {
    field: 'name',
    direction: 'asc',
  },
  renameMode: false,
  renameDraft: null,
  pendingArchiveConfirm: false,
  pendingDeleteConfirm: false,
  createProjectModalOpen: false,
  createProjectDraft: {
    name: '',
    displayColour: '',
    defaultBillable: false,
  },
  submitInProgress: false,
  lastErrorKey: null,
};

export const adminClientDetailPageSlice = createSlice({
  name: 'adminClientDetailPage',
  initialState,
  reducers: {
    // ── UC-39 rename ──────────────────────────────────────────────────────
    enterRenameMode(state) {
      state.renameMode = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    exitRenameMode(state) {
      state.renameMode = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setSubmitInProgress(state, action: PayloadAction<boolean>) {
      state.submitInProgress = action.payload;
    },
    setLastErrorKey(state, action: PayloadAction<string | null>) {
      state.lastErrorKey = action.payload;
    },
    // ── UC-40 archive confirm ─────────────────────────────────────────────
    // Opens the confirmation dialog; clears any leftover submit/error state
    // from a previous interaction so the dialog always opens clean.
    openArchiveConfirm(state) {
      state.pendingArchiveConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // Closes the dialog; clears submit/error state.
    closeArchiveConfirm(state) {
      state.pendingArchiveConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-41 delete confirm ──────────────────────────────────────────────
    openDeleteConfirm(state) {
      state.pendingDeleteConfirm = true;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeDeleteConfirm(state) {
      state.pendingDeleteConfirm = false;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    // ── UC-42 Projects sub-section ────────────────────────────────────────
    setProjectsNameSearch(state, action: PayloadAction<string>) {
      state.projectsFilters.nameSearch = action.payload;
    },
    setProjectsArchiveStatus(state, action: PayloadAction<ArchiveStatusFilter>) {
      state.projectsFilters.archiveStatus = action.payload;
    },
    setProjectsBillableDefault(state, action: PayloadAction<BillableFilter>) {
      state.projectsFilters.billableDefault = action.payload;
    },
    clearProjectsFilters(state) {
      state.projectsFilters = initialState.projectsFilters;
    },
    toggleProjectsNameSort(state) {
      state.projectsSort.direction =
        state.projectsSort.direction === 'asc' ? 'desc' : 'asc';
    },
    openCreateProjectModal(state) {
      state.createProjectModalOpen = true;
      state.createProjectDraft = initialState.createProjectDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    closeCreateProjectModal(state) {
      state.createProjectModalOpen = false;
      state.createProjectDraft = initialState.createProjectDraft;
      state.submitInProgress = false;
      state.lastErrorKey = null;
    },
    setCreateProjectDraftName(state, action: PayloadAction<string>) {
      state.createProjectDraft.name = action.payload;
    },
    setCreateProjectDraftColour(state, action: PayloadAction<string>) {
      state.createProjectDraft.displayColour = action.payload;
    },
    setCreateProjectDraftBillable(state, action: PayloadAction<boolean>) {
      state.createProjectDraft.defaultBillable = action.payload;
    },
    resetPage() {
      return initialState;
    },
  },
});

export const {
  enterRenameMode,
  exitRenameMode,
  setSubmitInProgress,
  setLastErrorKey,
  openArchiveConfirm,
  closeArchiveConfirm,
  openDeleteConfirm,
  closeDeleteConfirm,
  setProjectsNameSearch,
  setProjectsArchiveStatus,
  setProjectsBillableDefault,
  clearProjectsFilters,
  toggleProjectsNameSort,
  openCreateProjectModal,
  closeCreateProjectModal,
  setCreateProjectDraftName,
  setCreateProjectDraftColour,
  setCreateProjectDraftBillable,
  resetPage,
} = adminClientDetailPageSlice.actions;

type AppRootState = {
  adminClientDetailPage: AdminClientDetailPageState;
};

export const selectAdminClientDetailPage = (state: AppRootState) =>
  state.adminClientDetailPage;
